import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel
} from '@mui/material';
import {UserActivityTypes} from "../../types";
import {When} from "react-if";
import Loading from "../loading";

interface Props{
    loading : boolean;
    data: UserActivityTypes.ActiveUserData[]
    onRowClick : (teamName : string) => void;
    onSort?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortOrder?: 'ASC' | 'DESC';
}

type SortableColumn = 'teamName' | 'activeCount' | 'inactiveCount' | 'activePercentage';

interface ColumnConfig {
    key: SortableColumn;
    label: string;
    align: 'left' | 'center' | 'right';
    sortable: boolean;
}

const columns: ColumnConfig[] = [
    { key: 'teamName', label: 'Team Name', align: 'left', sortable: true },
    { key: 'activeCount', label: 'Count of Active', align: 'center', sortable: true },
    { key: 'inactiveCount', label: 'Count of Inactive', align: 'center', sortable: true },
    { key: 'activePercentage', label: '% of Total Active', align: 'center', sortable: true }
];

export const ActiveUserPerTeamTable: React.FC<Props> = ({
                                                            data,
                                                            loading,
                                                            onRowClick,
                                                            onSort,
                                                            currentSortBy = 'teamName',
                                                            currentSortOrder = 'ASC'
                                                        }) => {
    const [selectedRow, setSelectedRow] = React.useState<number | null>(null);

    const handleRowClick = (teamId: string, index: number) => {
        setSelectedRow(index);
        onRowClick(teamId);
    };

    const handleSort = (column: SortableColumn) => {
        if (!onSort) return;

        let newSortOrder: 'ASC' | 'DESC' = 'ASC';

        if (currentSortBy === column) {
            // Toggle sort order if clicking the same column
            newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            // Set new column and default to ascending
            newSortOrder = 'ASC';
        }

        onSort(column, newSortOrder);
    };

    return (
        <div style={{ width: '100%', height: 400, overflow: 'auto' }}>
            <When
                condition={
                    !loading && (!data || data.length === 0)
                }
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', margin: 'auto' }}>
                    <Typography align={"center"} fontSize={14} fontWeight={600}>
                        No data available
                    </Typography>
                </Box>
            </When>
            <When condition={data?.length > 0}>
                <TableContainer sx={{ borderRadius: 0, maxHeight: 400, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ borderBottom: '4px solid white' }} >
                                {columns.map((column) => (
                                    <TableCell
                                        sx={{
                                            backgroundColor: '#BDBABA !important',
                                            backgroundImage: 'none',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            padding: '8px 16px'
                                        }}
                                        key={column.key}
                                        align={column.align === 'left' ? 'inherit' : column.align}
                                    >
                                        {column.sortable && onSort ? (
                                            <TableSortLabel
                                                active={currentSortBy === column.key}
                                                direction={currentSortBy === column.key ? currentSortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                                                onClick={!loading ? () => handleSort(column.key) : undefined}
                                                sx={{
                                                    '& .MuiTableSortLabel-root': {
                                                        color: '#000000',
                                                        fontWeight: 700,
                                                        fontSize: 14
                                                    },
                                                    '& .MuiTableSortLabel-root:hover': {
                                                        color: '#333333'
                                                    },
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: '#000000 !important'
                                                    }
                                                }}
                                            >
                                                <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                    {column.label}
                                                </Typography>
                                            </TableSortLabel>
                                        ) : (
                                            <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                {column.label}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading}>
                            <TableBody>
                                {data.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        onClick={() => handleRowClick(row.teamId, index)}
                                        sx={{
                                            borderBottom: '4px solid white',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#F5F4F4'
                                            },
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#DDF5FF'
                                            },
                                            ":hover": {
                                                backgroundColor: '#dbd8d8',
                                                cursor: 'pointer'
                                            },
                                            ...(selectedRow === index && {
                                                backgroundColor: '#11A7E2 !important'
                                            })
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.teamName}
                                        </TableCell>
                                        <TableCell align="center">{row.activeCount}</TableCell>
                                        <TableCell align="center">{row.inactiveCount}</TableCell>
                                        <TableCell align="center">{Math.round(Number(row.activePercentage))}&#37;</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>

                    </Table>
                    <When condition={loading}>
                        <Loading />
                    </When>
                </TableContainer>
            </When>
        </div>
    );
}